import { useEffect, useState } from "react";
import {
  MessageSquareText,
  FileText,
  CheckCircle2,
  Plane,
  Users,
  ShieldCheck,
  KeyRound,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const KPI_ICONS = {
  active_inquiries: MessageSquareText,
  unassigned_quotes: FileText,
  confirmed_bookings: CheckCircle2,
  active_trips: Plane,
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/summary")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" />
      </div>
    );
  }
  if (!data) return null;

  const foundation = [
    { label: "Users", value: data.foundation.users, icon: Users, tint: "sky" },
    { label: "Roles", value: data.foundation.roles, icon: ShieldCheck, tint: "amber" },
    { label: "Permissions", value: data.foundation.permissions, icon: KeyRound, tint: "emerald" },
  ];

  const tintMap = {
    sky: "bg-sky-50 text-sky-600 dark:bg-ink-gold/10 dark:text-ink-gold",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Operations Dashboard</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text sm:text-3xl">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {data.company.brand_name} · reporting in {data.company.default_currency}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.kpis.map((k) => {
          const Icon = KPI_ICONS[k.key] || FileText;
          return (
            <div
              key={k.key}
              data-testid={`kpi-card-${k.key}`}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-ink-line dark:bg-ink-card"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{k.label}</span>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-ink-panel">
                  <Icon className="h-[18px] w-[18px]" />
                </div>
              </div>
              <div className="mt-3 font-mono-tf text-3xl font-bold text-slate-900 dark:text-ink-text">{k.value}</div>
              <div className="mt-1 text-[11px] font-medium text-slate-400">{k.hint}</div>
            </div>
          );
        })}
      </div>

      {/* Workflow pipeline */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card sm:p-6" data-testid="workflow-pipeline">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">Operations Workflow</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">The end-to-end pipeline — business modules arrive in later phases.</p>
          </div>
        </div>
        <div className="flex flex-nowrap gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-ink-line dark:bg-ink/40">
          {data.workflow_stages.map((stage, i) => (
            <div key={stage} className="flex shrink-0 items-center gap-2">
              <div
                data-testid={`workflow-stage-${i + 1}`}
                className={`flex items-center gap-2 whitespace-nowrap rounded-lg border px-3 py-2 text-xs font-medium ${
                  i === 0
                    ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-ink-gold/30 dark:bg-ink-gold/10 dark:text-ink-gold"
                    : "border-slate-200 bg-white text-slate-500 dark:border-ink-line dark:bg-ink-card dark:text-slate-400"
                }`}
              >
                <span className="font-mono-tf text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                {stage}
              </div>
              {i < data.workflow_stages.length - 1 && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />}
            </div>
          ))}
        </div>
      </div>

      {/* Foundation status */}
      <div>
        <h2 className="mb-3 font-display text-lg font-semibold text-slate-900 dark:text-ink-text">Foundation Status</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {foundation.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid={`foundation-${f.label.toLowerCase()}`}>
                <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tintMap[f.tint]}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-mono-tf text-2xl font-bold text-slate-900 dark:text-ink-text">{f.value}</div>
                  <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{f.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
